import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, 
  Upload, 
  Download, 
  Lock, 
  Unlock, 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  Minimize, 
  BarChart3,
  Key,
  FileText,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Trash2,
  Copy,
  RefreshCw,
  Clock,
  FileLock
} from 'lucide-react';

interface AnalyticsData {
  operationsPerformed: number;
  totalFilesProcessed: number;
  successRate: number;
  averageTime: number;
  totalFileSize: number;
}

interface SettingsData {
  encryptionLevel: string;
  permissions: string[];
  ownerPasswordRequired: boolean;
  userPasswordRequired: boolean;
  keyLength: string;
  autoDownload: boolean;
  showTimestamp: boolean;
}

interface ResultData {
  fileName: string;
  operation: string;
  timestamp: string;
  size: number;
  downloadUrl: string;
}

const PDFEncryptDecrypt = () => {
  const [isDark, setIsDark] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [ownerPassword, setOwnerPassword] = useState<string>('');
  const [userPassword, setUserPassword] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [processing, setProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<ResultData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    operationsPerformed: 0,
    totalFilesProcessed: 0,
    successRate: 100,
    averageTime: 0,
    totalFileSize: 0
  });
  const [settings, setSettings] = useState<SettingsData>({
    encryptionLevel: 'AES-256',
    permissions: ['print', 'copy'],
    ownerPasswordRequired: true,
    userPasswordRequired: false,
    keyLength: '256',
    autoDownload: true,
    showTimestamp: true
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate random password
  const generatePassword = useCallback((length: number = 12): string => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }, []);

  // Analytics tracking
  const trackOperation = useCallback((type: string, success: boolean, fileSize: number, duration: number) => {
    setAnalytics(prev => ({
      operationsPerformed: prev.operationsPerformed + 1,
      totalFilesProcessed: prev.totalFilesProcessed + (success ? 1 : 0),
      successRate: ((prev.successRate * prev.operationsPerformed + (success ? 100 : 0)) / (prev.operationsPerformed + 1)),
      averageTime: ((prev.averageTime * prev.operationsPerformed + duration) / (prev.operationsPerformed + 1)),
      totalFileSize: prev.totalFileSize + (success ? fileSize : 0)
    }));
  }, []);

  // Simulate PDF encryption/decryption
  const processPDF = useCallback(async () => {
    if (!selectedFile) return;

    // Validate passwords based on operation
    if (operation === 'encrypt') {
      if (settings.ownerPasswordRequired && !ownerPassword.trim()) {
        alert('Owner password is required for encryption');
        return;
      }
      if (settings.userPasswordRequired && !userPassword.trim()) {
        alert('User password is required for encryption');
        return;
      }
    } else {
      if (!ownerPassword.trim() && !userPassword.trim()) {
        alert('At least one password is required for decryption');
        return;
      }
    }

    setProcessing(true);
    const startTime = Date.now();

    try {
      // Simulate processing time (PDF operations are typically slower)
      await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 4000));

      // Simulate PDF processing
      const fileReader = new FileReader();
      
      fileReader.onload = async (e) => {
        if (!e.target?.result) throw new Error('Failed to read file');

        let processedContent: ArrayBuffer;
        
        if (operation === 'encrypt') {
          // Simulate PDF encryption
          const originalBuffer = e.target.result as ArrayBuffer;
          const originalArray = new Uint8Array(originalBuffer);
          
          // Simple encryption simulation (XOR with password-derived key)
          const key = new TextEncoder().encode(ownerPassword || userPassword);
          const encrypted = new Uint8Array(originalArray.length + 100); // Add metadata space
          
          // Add PDF encryption header simulation
          const header = new TextEncoder().encode(`%PDF-ENCRYPTED-${settings.encryptionLevel}-`);
          encrypted.set(header, 0);
          
          // Encrypt content
          for (let i = 0; i < originalArray.length; i++) {
            encrypted[i + header.length] = originalArray[i] ^ key[i % key.length];
          }
          
          processedContent = encrypted.buffer;
        } else {
          // Simulate PDF decryption
          const encryptedBuffer = e.target.result as ArrayBuffer;
          const encryptedArray = new Uint8Array(encryptedBuffer);
          
          // Check for encryption header
          const headerText = new TextDecoder().decode(encryptedArray.slice(0, 20));
          if (!headerText.includes('PDF-ENCRYPTED')) {
            throw new Error('File does not appear to be encrypted or wrong format');
          }
          
          // Find header end and decrypt
          let headerEnd = 0;
          for (let i = 0; i < encryptedArray.length - 1; i++) {
            if (encryptedArray[i] === 45 && encryptedArray[i + 1] !== 45) { // Find last '-'
              headerEnd = i + 1;
              break;
            }
          }
          
          const key = new TextEncoder().encode(ownerPassword || userPassword);
          const decrypted = new Uint8Array(encryptedArray.length - headerEnd);
          
          // Decrypt content
          for (let i = 0; i < decrypted.length; i++) {
            decrypted[i] = encryptedArray[i + headerEnd] ^ key[i % key.length];
          }
          
          processedContent = decrypted.buffer;
        }

        // Create blob and download URL
        const blob = new Blob([processedContent], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(blob);
        const fileName = `${operation}ed_${selectedFile.name}`;

        const duration = Date.now() - startTime;
        const timestamp = new Date().toLocaleString();

        setResult({
          fileName,
          operation,
          timestamp,
          size: blob.size,
          downloadUrl
        });

        trackOperation(operation, true, selectedFile.size, duration);

        if (settings.autoDownload) {
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = fileName;
          a.click();
        }
      };

      fileReader.onerror = () => {
        throw new Error('Failed to read file');
      };

      fileReader.readAsArrayBuffer(selectedFile);

    } catch (error) {
      const duration = Date.now() - startTime;
      trackOperation(operation, false, selectedFile.size, duration);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error: ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  }, [selectedFile, ownerPassword, userPassword, operation, settings, trackOperation]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadFile = (url: string, filename: string): void => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const clearInputs = (): void => {
    setSelectedFile(null);
    setOwnerPassword('');
    setUserPassword('');
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const togglePermission = (permission: string): void => {
    setSettings(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  return (
    <>
      <Helmet>
        <title>PDF Encrypt & Decrypt - Document Security Tool</title>
        <meta name="description" content="Professional PDF encryption and decryption tool with password protection, permission controls, and AES-256 security." />
        <meta name="keywords" content="PDF encryption, PDF decryption, document security, password protection, PDF permissions, AES encryption" />
        <meta name="author" content="Document Security Tools" />
        <meta property="og:title" content="PDF Encrypt & Decrypt Tool" />
        <meta property="og:description" content="Secure your PDF documents with professional encryption and permission controls." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://example.com/pdf-encrypt-decrypt" />
      </Helmet>

      <div 
        ref={containerRef}
        className={`min-h-screen transition-all duration-300 ${
          isDark ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-pink-50 to-rose-100 text-gray-900'
        } ${isFullscreen ? 'p-0' : 'p-4'}`}
      >
        {/* Header */}
        <header className="flex items-center justify-between mb-8 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
          <div className="flex items-center space-x-3">
            <FileLock className="w-8 h-8 text-pink-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
              PDF Encrypt & Decrypt
            </h1>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showAnalytics ? 'bg-pink-500 text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              <BarChart3 className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showSettings ? 'bg-pink-500 text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-lg transition-colors text-gray-700 dark:text-gray-300 ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              }`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors text-gray-700 dark:text-gray-300 ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              }`}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Panel */}
          <div className="lg:col-span-2">
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              
              {/* Operation Toggle */}
              <div className="flex mb-6 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <button
                  onClick={() => setOperation('encrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'encrypt'
                      ? 'bg-pink-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Encrypt PDF
                </button>
                <button
                  onClick={() => setOperation('decrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'decrypt'
                      ? 'bg-pink-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Decrypt PDF
                </button>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Select PDF File
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={clearInputs}
                      className="text-xs px-2 py-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" />
                      Clear
                    </button>
                  </div>
                </div>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    selectedFile
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : `border-gray-300 dark:border-gray-600 hover:border-pink-500 ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-pink-50'
                        }`
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files[0]) {
                        setSelectedFile(files[0]);
                      }
                    }}
                    className="hidden"
                  />
                  
                  {selectedFile ? (
                    <div className="flex items-center justify-center space-x-3">
                      <FileText className="w-8 h-8 text-green-500" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(selectedFile.size)}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clearInputs();
                        }}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium mb-2 text-gray-900 dark:text-white">Drop your PDF file here</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">or click to browse</p>
                      <p className="text-xs text-gray-400 mt-2">Supports PDF files up to 100MB</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Password Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Key className="w-4 h-4 inline mr-1" />
                    Password Protection
                  </label>
                  <button
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="text-xs px-3 py-1 rounded bg-pink-100 dark:bg-pink-900 text-pink-600 dark:text-pink-300 hover:bg-pink-200 dark:hover:bg-pink-800 transition-colors"
                  >
                    {showPasswords ? <EyeOff className="w-3 h-3 inline mr-1" /> : <Eye className="w-3 h-3 inline mr-1" />}
                    {showPasswords ? 'Hide' : 'Show'} Passwords
                  </button>
                </div>

                {/* Owner Password */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Owner Password {operation === 'encrypt' && settings.ownerPasswordRequired && <span className="text-red-500">*</span>}
                    </label>
                    <button
                      onClick={() => setOwnerPassword(generatePassword())}
                      className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3 inline mr-1" />
                      Generate
                    </button>
                  </div>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    placeholder="Enter owner password (full permissions)"
                    className={`w-full px-3 py-2 rounded border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-pink-500 focus:border-transparent`}
                  />
                </div>

                {/* User Password */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                      User Password {operation === 'encrypt' && settings.userPasswordRequired && <span className="text-red-500">*</span>}
                    </label>
                    <button
                      onClick={() => setUserPassword(generatePassword())}
                      className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3 inline mr-1" />
                      Generate
                    </button>
                  </div>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    placeholder="Enter user password (restricted permissions)"
                    className={`w-full px-3 py-2 rounded border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-pink-500 focus:border-transparent`}
                  />
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p>• Owner password: Full document access and permissions</p>
                  <p>• User password: Restricted access based on permissions</p>
                </div>
              </div>

              {/* Process Button */}
              <button
                onClick={processPDF}
                disabled={!selectedFile || processing}
                className={`w-full py-4 px-6 rounded-lg font-medium transition-all transform ${
                  !selectedFile || processing
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:from-pink-600 hover:to-rose-700 hover:scale-105 shadow-lg'
                }`}
              >
                {processing ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Processing PDF...
                  </div>
                ) : (
                  `${operation === 'encrypt' ? 'Encrypt' : 'Decrypt'} PDF File`
                )}
              </button>

              {/* Result */}
              {result && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span className="font-medium text-green-700 dark:text-green-300">
                        PDF {result.operation === 'encrypt' ? 'Encrypted' : 'Decrypted'} Successfully!
                      </span>
                    </div>
                    {settings.showTimestamp && (
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {result.timestamp}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">{result.fileName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(result.size)}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => downloadFile(result.downloadUrl, result.fileName)}
                        className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Analytics Panel */}
            {showAnalytics && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Analytics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Operations</span>
                    <span className="font-medium text-gray-900 dark:text-white">{analytics.operationsPerformed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Files Processed</span>
                    <span className="font-medium text-gray-900 dark:text-white">{analytics.totalFilesProcessed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total Size</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatFileSize(analytics.totalFileSize)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Success Rate</span>
                    <span className="font-medium text-green-500">{analytics.successRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Avg. Time</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(analytics.averageTime / 1000).toFixed(1)}s</span>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
                  <Settings className="w-5 h-5 mr-2" />
                  PDF Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Encryption Level</label>
                    <select
                      value={settings.encryptionLevel}
                      onChange={(e) => setSettings(prev => ({ ...prev, encryptionLevel: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="RC4-40">RC4 40-bit (Legacy)</option>
                      <option value="AES-128">AES 128-bit</option>
                      <option value="AES-256">AES 256-bit (Recommended)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Document Permissions</label>
                    <div className="space-y-2">
                      {['print', 'copy', 'modify', 'annotate', 'form-fill', 'extract', 'assemble'].map((permission) => (
                        <label key={permission} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.permissions.includes(permission)}
                            onChange={() => togglePermission(permission)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                            {permission.replace('-', ' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Key Length</label>
                    <select
                      value={settings.keyLength}
                      onChange={(e) => setSettings(prev => ({ ...prev, keyLength: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="128">128-bit</option>
                      <option value="256">256-bit</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.ownerPasswordRequired}
                        onChange={(e) => setSettings(prev => ({ ...prev, ownerPasswordRequired: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Require owner password</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.userPasswordRequired}
                        onChange={(e) => setSettings(prev => ({ ...prev, userPasswordRequired: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Require user password</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.autoDownload}
                        onChange={(e) => setSettings(prev => ({ ...prev, autoDownload: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Auto-download result</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.showTimestamp}
                        onChange={(e) => setSettings(prev => ({ ...prev, showTimestamp: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show timestamps</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* PDF Security Info */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                <AlertCircle className="w-5 h-5 mr-2 text-pink-500" />
                PDF Security
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>Owner Password:</strong> Full document control</p>
                <p>• <strong>User Password:</strong> View with restrictions</p>
                <p>• <strong>AES-256:</strong> Industry standard encryption</p>
                <p>• <strong>Permissions:</strong> Control user actions</p>
                <p>• <strong>Metadata:</strong> Protected from extraction</p>
                <p>• <strong>Standards:</strong> PDF 1.4+ compatible</p>
              </div>
            </div>

            {/* Permission Guide */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                <Shield className="w-5 h-5 mr-2 text-pink-500" />
                Permission Guide
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>Print:</strong> Allow document printing</p>
                <p>• <strong>Copy:</strong> Enable text/image copying</p>
                <p>• <strong>Modify:</strong> Edit document content</p>
                <p>• <strong>Annotate:</strong> Add comments/annotations</p>
                <p>• <strong>Form Fill:</strong> Fill interactive forms</p>
                <p>• <strong>Extract:</strong> Extract pages/content</p>
                <p>• <strong>Assemble:</strong> Insert/rotate/delete pages</p>
              </div>
            </div>

            {/* Encryption Standards */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                <FileLock className="w-5 h-5 mr-2 text-pink-500" />
                Encryption Standards
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>RC4 40-bit:</strong> Legacy, weak security</p>
                <p>• <strong>RC4 128-bit:</strong> Better but deprecated</p>
                <p>• <strong>AES 128-bit:</strong> Strong, widely supported</p>
                <p>• <strong>AES 256-bit:</strong> Maximum security level</p>
                <p>• <strong>PDF 2.0:</strong> Latest security features</p>
                <p>• <strong>Compliance:</strong> GDPR, HIPAA compatible</p>
              </div>
            </div>

            {/* Password Security */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                <Key className="w-5 h-5 mr-2 text-green-500" />
                Password Best Practices
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>Length:</strong> Use 12+ characters</p>
                <p>• <strong>Complexity:</strong> Mix letters, numbers, symbols</p>
                <p>• <strong>Uniqueness:</strong> Don't reuse passwords</p>
                <p>• <strong>Storage:</strong> Use password managers</p>
                <p>• <strong>Sharing:</strong> Secure transmission only</p>
                <p>• <strong>Recovery:</strong> Keep secure backups</p>
              </div>
            </div>

            {/* File Security Notes */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                <FileText className="w-5 h-5 mr-2 text-orange-500" />
                Security Notes
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• Files are processed locally in your browser</p>
                <p>• No data is sent to external servers</p>
                <p>• Original files remain unchanged</p>
                <p>• Temporary files are automatically cleaned</p>
                <p>• This is a demonstration tool</p>
                <p>• Use professional tools for sensitive documents</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PDFEncryptDecrypt;